import axios from 'src/utils/axios';
import { endpoints } from 'src/utils/api-endpoints';
import { Skill } from 'src/models/skill';
import { Topic } from 'src/models/topic';
import { SubTopic } from 'src/models/sub-topic';
import { AppResponse } from 'src/models/app-response';
import { Resource } from 'src/models/resource';

// ── Skill ──────────────────────────────────────────────────────────────────

export async function getSkillsWithBasicData(): Promise<AppResponse<Omit<Skill, 'items'>[]>> {
  try {
    const response = await axios.get(endpoints.skill.getSkillsWithBasicData);
    return response.data;
  } catch (error) {
    console.error('Failed to get skills:', error);
    return { success: false, data: [], error };
  }
}

export async function getSkillById(skillId: string): Promise<AppResponse<Skill>> {
  try {
    const response = await axios.get(endpoints.skill.getById(skillId));
    return response.data;
  } catch (error) {
    console.error('Failed to get skill by id:', error);
    return { success: false, data: undefined, error };
  }
}

export async function createSkill(skill: Omit<Skill, '_id' | 'organization'>): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.post(endpoints.skill.create, skill);
    return response.data;
  } catch (error) {
    console.error('Failed to create skill:', error);
    return { success: false, data: null, error };
  }
}

export async function editSkill(
  skill: Partial<Skill> & { _id: string }
): Promise<AppResponse<Skill | null>> {
  try {
    const { _id, ...body } = skill; 
    delete body.organization;
    const response = await axios.put(endpoints.skill.edit(_id), body);
    return response.data;
  } catch (error) {
    console.error('Failed to edit skill:', error);
    return { success: false, data: null, error };
  }
}

export async function deleteSkill(skillId: string): Promise<AppResponse<void>> {
  try {
    const response = await axios.delete(endpoints.skill.delete(skillId));
    return response.data;
  } catch (error) {
    console.error('Failed to delete skill:', error);
    return { success: false, data: undefined, error };
  }
}

export async function duplicateSkill(skillId: string): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.post(endpoints.skill.duplicate(skillId));
    return response.data;
  } catch (error) {
    console.error('Failed to duplicate skill:', error);
    return { success: false, data: null, error };
  }
}

// ── Topics ─────────────────────────────────────────────────────────────────

export async function addTopic(
  skillId: string,
  topic: Omit<Topic, '_id'>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.post(endpoints.skill.addTopic(skillId), topic);
    return response.data;
  } catch (error) {
    console.error('Failed to add topic:', error);
    return { success: false, data: null, error };
  }
}

export async function updateTopic(
  skillId: string,
  topicId: string,
  data: Partial<Omit<Topic, 'subItems' | 'resources'>>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.put(endpoints.skill.updateTopic(skillId, topicId), data);
    return response.data;
  } catch (error) {
    console.error('Failed to update topic:', error);
    return { success: false, data: null, error };
  }
}

export async function deleteTopic(
  skillId: string,
  topicId: string
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.delete(endpoints.skill.deleteTopic(skillId, topicId));
    return response.data;
  } catch (error) {
    console.error('Failed to delete topic:', error);
    return { success: false, data: null, error };
  }
}

export async function reorderTopics(
  skillId: string,
  topicIds: string[]
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.put(endpoints.skill.reorderTopics(skillId), { topicIds });
    return response.data;
  } catch (error) {
    console.error('Failed to reorder topics:', error);
    return { success: false, data: null, error };
  }
}

// ── SubTopics ──────────────────────────────────────────────────────────────

export async function addSubTopic(
  skillId: string,
  topicId: string,
  subTopic: Omit<SubTopic, '_id'>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.post(endpoints.skill.addSubTopic(skillId, topicId), subTopic);
    return response.data;
  } catch (error) {
    console.error('Failed to add subtopic:', error);
    return { success: false, data: null, error };
  }
}

export async function updateSubTopic(
  skillId: string,
  topicId: string,
  subTopicId: string,
  data: Partial<SubTopic>
): Promise<AppResponse<Skill | null>> {
  try {
    delete data.resources;
    const response = await axios.put(
      endpoints.skill.updateSubTopic(skillId, topicId, subTopicId),
      data
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update subtopic:', error);
    return { success: false, data: null, error };
  }
}

export async function deleteSubTopic(
  skillId: string,
  topicId: string,
  subTopicId: string
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.delete(
      endpoints.skill.deleteSubTopic(skillId, topicId, subTopicId)
    );
    return response.data;
  } catch (error) {
    console.error('Failed to delete subtopic:', error);
    return { success: false, data: null, error };
  }
}

export async function reorderSubTopics(
  skillId: string,
  topicId: string,
  subtopicIds: string[]
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.put(
      endpoints.skill.reorderSubTopics(skillId, topicId),
      { subtopicIds }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to reorder subtopics:', error);
    return { success: false, data: null, error };
  }
}

// ── Topic Resources ────────────────────────────────────────────────────────

export async function addTopicResource(
  skillId: string, topicId: string, data: Omit<Resource, '_id'>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.post(endpoints.skill.addTopicResource(skillId, topicId), data);
    return response.data;
  } catch (error) {
    return { success: false, data: null, error };
  }
}

export async function updateTopicResource(
  skillId: string, topicId: string, resourceId: string, data: Partial<Resource>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.put(endpoints.skill.updateTopicResource(skillId, topicId, resourceId), data);
    return response.data;
  } catch (error) {
    return { success: false, data: null, error };
  }
}

export async function deleteTopicResource(
  skillId: string, topicId: string, resourceId: string
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.delete(endpoints.skill.deleteTopicResource(skillId, topicId, resourceId));
    return response.data;
  } catch (error) {
    return { success: false, data: null, error };
  }
}

// ── SubTopic Resources ─────────────────────────────────────────────────────

export async function addSubTopicResource(
  skillId: string, topicId: string, subId: string, data: Omit<Resource, '_id'>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.post(endpoints.skill.addSubTopicResource(skillId, topicId, subId), data);
    return response.data;
  } catch (error) {
    return { success: false, data: null, error };
  }
}

export async function updateSubTopicResource(
  skillId: string, topicId: string, subId: string, resourceId: string, data: Partial<Resource>
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.put(endpoints.skill.updateSubTopicResource(skillId, topicId, subId, resourceId), data);
    return response.data;
  } catch (error) {
    return { success: false, data: null, error };
  }
}

export async function deleteSubTopicResource(
  skillId: string, topicId: string, subId: string, resourceId: string
): Promise<AppResponse<Skill | null>> {
  try {
    const response = await axios.delete(endpoints.skill.deleteSubTopicResource(skillId, topicId, subId, resourceId));
    return response.data;
  } catch (error) {
    return { success: false, data: null, error };
  }
}